<template>
  <div class="schedule-editor">
    <div class="row">
      <div class="col-md-6">
        <label>Frequency</label>
        <select class="form-control" v-model="frequency" @change="onFrequencyChange">
          <option value="daily">Daily</option>
          <option value="weekdays">Weekdays (Mon-Fri)</option>
          <option value="weekends">Weekends (Sat-Sun)</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="interval">Every X minutes</option>
        </select>
      </div>
    </div>

    <!-- Day of Week (for Weekly) -->
    <div class="row mt-3" v-if="frequency === 'weekly'">
      <div class="col-md-6">
        <label>Day of Week</label>
        <select class="form-control" v-model="selectedDayOfWeek">
          <option value="MONDAY">Monday</option>
          <option value="TUESDAY">Tuesday</option>
          <option value="WEDNESDAY">Wednesday</option>
          <option value="THURSDAY">Thursday</option>
          <option value="FRIDAY">Friday</option>
          <option value="SATURDAY">Saturday</option>
          <option value="SUNDAY">Sunday</option>
        </select>
      </div>
    </div>

    <!-- Day of Month (for Monthly) -->
    <div class="row mt-3" v-if="frequency === 'monthly'">
      <div class="col-md-6">
        <label>Day of Month</label>
        <select class="form-control" v-model="selectedDayOfMonth">
          <option value="first">First day</option>
          <option value="last">Last day</option>
          <option v-for="day in 30" :key="day" :value="day">{{ day }}</option>
        </select>
      </div>
    </div>

    <!-- Time picker (for non-interval schedules) -->
    <div class="row mt-3" v-if="frequency !== 'interval'">
      <div class="col-md-3">
        <label>Hour</label>
        <select class="form-control" v-model.number="selectedHour">
          <option v-for="hour in 24" :key="hour - 1" :value="hour - 1">
            {{ (hour - 1).toString().padStart(2, '0') }}:00
          </option>
        </select>
      </div>
      <div class="col-md-3">
        <label>Minute</label>
        <select class="form-control" v-model.number="selectedMinute">
          <option v-for="minute in minuteOptions" :key="minute" :value="minute">
            :{{ minute.toString().padStart(2, '0') }}
          </option>
        </select>
      </div>
    </div>

    <!-- Interval settings (for "Every X minutes") -->
    <div class="row mt-3" v-if="frequency === 'interval'">
      <div class="col-md-4">
        <label>Run every</label>
        <select class="form-control" v-model.number="intervalMinutes">
          <option :value="5">5 minutes</option>
          <option :value="10">10 minutes</option>
          <option :value="15">15 minutes</option>
          <option :value="30">30 minutes</option>
          <option :value="60">1 hour</option>
        </select>
      </div>
      <div class="col-md-4">
        <label>Starting at minute</label>
        <select class="form-control" v-model.number="intervalOffset">
          <option v-for="minute in 60" :key="minute - 1" :value="minute - 1">
            :{{ (minute - 1).toString().padStart(2, '0') }}
          </option>
        </select>
      </div>
    </div>

    <!-- Preview -->
    <div class="row mt-3">
      <div class="col-md-12">
        <div class="alert alert-info mb-0">
          <strong>Schedule:</strong> {{ scheduleDescription }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ScheduleEditor",
  props: {
    value: {
      type: Object,
      default: () => null
    }
  },
  data() {
    const now = new Date();
    return {
      frequency: "daily",
      selectedDayOfWeek: "MONDAY",
      selectedDayOfMonth: 1,
      selectedHour: now.getHours(),
      selectedMinute: Math.floor(now.getMinutes() / 5) * 5, 
      intervalMinutes: 15,
      intervalOffset: 0,
      minuteOptions: [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]
    };
  },
  computed: {
    scheduleJson() {
      const schedule = {
        dayOfMonth: "any",
        dayOfWeek: "any",
        month: "any",
        hour: "any",
        minute: "any"
      };

      switch (this.frequency) {
        case "daily":
          schedule.hour = this.selectedHour;
          schedule.minute = this.selectedMinute;
          break;
        case "weekdays":
          schedule.dayOfWeek = "weekDays";
          schedule.hour = this.selectedHour;
          schedule.minute = this.selectedMinute;
          break;
        case "weekends":
          schedule.dayOfWeek = "weekendDays ";
          schedule.hour = this.selectedHour;
          schedule.minute = this.selectedMinute;
          break;
        case "weekly":
          schedule.dayOfWeek = this.selectedDayOfWeek;
          schedule.hour = this.selectedHour;
          schedule.minute = this.selectedMinute;
          break;
        case "monthly":
          if (this.selectedDayOfMonth === "first") {
            schedule.dayOfMonth = "first";
          } else if (this.selectedDayOfMonth === "last") {
            schedule.dayOfMonth = "last";
          } else {
            schedule.dayOfMonth = this.selectedDayOfMonth;
          }
          schedule.hour = this.selectedHour;
          schedule.minute = this.selectedMinute;
          break;
        case "interval":
          schedule.minute = {
            interval: this.intervalMinutes,
            offset: this.intervalOffset
          };
          break;
      }

      return schedule;
    },
    scheduleDescription() {
      return this.formatSchedule(this.scheduleJson);
    }
  },
  watch: {
    scheduleJson: {
      handler(newVal) {
        this.$emit("input", newVal);
      },
      deep: true,
      immediate: true
    },
    value: {
      handler(newVal) {
        if (newVal) {
          this.parseSchedule(newVal);
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    if (!this.value) {
      this.$emit("input", this.scheduleJson);
    }
  },
  methods: {
    onFrequencyChange() {
      if (this.frequency === "interval") {
        this.intervalMinutes = 15;
        this.intervalOffset = 0;
      }
    },
    parseSchedule(schedule) {
      if (!schedule) return;
      if (schedule.minute && typeof schedule.minute === "object" && schedule.minute.interval) {
        this.frequency = "interval";
        this.intervalMinutes = schedule.minute.interval;
        this.intervalOffset = schedule.minute.offset || 0;
      } else {
        if (schedule.hour !== "any") {
          this.selectedHour = schedule.hour;
        }
        if (schedule.minute !== "any" && typeof schedule.minute === "number") {
          this.selectedMinute = schedule.minute;
        }

        if (schedule.dayOfWeek === "weekDays") {
          this.frequency = "weekdays";
        } else if (schedule.dayOfWeek === "weekendDays " || schedule.dayOfWeek === "weekendDays") {
          this.frequency = "weekends";
        } else if (schedule.dayOfWeek !== "any") {
          this.frequency = "weekly";
          this.selectedDayOfWeek = schedule.dayOfWeek;
        } else if (schedule.dayOfMonth !== "any") {
          this.frequency = "monthly";
          this.selectedDayOfMonth = schedule.dayOfMonth;
        } else {
          this.frequency = "daily";
        }
      }
    },
    formatSchedule(schedule) {
      if (!schedule) return "Not configured";

      const parts = [];

      if (schedule.minute && typeof schedule.minute === "object" && schedule.minute.interval) {
        const interval = schedule.minute.interval;
        const offset = schedule.minute.offset || 0;
        if (interval === 60) {
          return `Every hour at :${offset.toString().padStart(2, "0")}`;
        }
        return `Every ${interval} minutes (starting at :${offset.toString().padStart(2, "0")})`;
      }

      let timeStr = "";
      if (schedule.hour !== "any" && schedule.minute !== "any") {
        const hour = schedule.hour.toString().padStart(2, "0");
        const minute = schedule.minute.toString().padStart(2, "0");
        timeStr = `at ${hour}:${minute}`;
      } else if (schedule.hour !== "any") {
        timeStr = `at ${schedule.hour.toString().padStart(2, "0")}:00`;
      }

      if (schedule.dayOfWeek === "weekDays") {
        parts.push("Weekdays");
      } else if (schedule.dayOfWeek === "weekendDays" || schedule.dayOfWeek === "weekendDays") {
        parts.push("Weekends");
      } else if (schedule.dayOfWeek !== "any") {
        const dayName = schedule.dayOfWeek.charAt(0) + schedule.dayOfWeek.slice(1).toLowerCase();
        parts.push(`Every ${dayName}`);
      } else if (schedule.dayOfMonth === "first") {
        parts.push("First day of month");
      } else if (schedule.dayOfMonth === "last") {
        parts.push("Last day of month");
      } else if (schedule.dayOfMonth !== "any") {
        parts.push(`Day ${schedule.dayOfMonth} of month`);
      } else {
        parts.push("Daily");
      }

      if (timeStr) {
        parts.push(timeStr);
      }

      return parts.join(" ");
    }
  }
};
</script>

<style scoped>
.schedule-editor {
  padding: 10px 0;
}
</style>
